import React, { useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, Input, NoticeBox, Tags } from "../../ui";
import { createChangeRequestAPI, editChangeRequestAPI, getApplicationDetailAPI } from "../../../utils/api/applications";
import BranchPicker from "./BranchPicker";
import { errorMessage } from "./applicationsText";
import { validateReason } from "./applicationsModel";

// Deactivation is a status-only request: it carries the application, the
// branches (or the whole application for the store) and a mandatory reason.
// It never edits account, manager or branch identifiers; those stay exactly as
// they are so the application can be reactivated later. The branches stay
// active until an administrator approves.
export default function DeactivateModal({ open, storeId, app, appName, t, token, request = null, scope = "branches", initialBranchIds = [], onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const wholeApp = (request ? request.scope : scope) === "store";
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !app) return undefined;
    let current = true;
    setLoading(true);
    setError("");
    setTouched(false);
    setSelected(request ? request.branchIds : []);
    setReason(request?.reason || "");
    getApplicationDetailAPI(token, storeId, app.key)
      .then((res) => {
        if (!current) return;
        const integration = res.data.integration;
        setDetail(integration);
        if (!request) {
          setSelected(integration.branches.filter((branch) => (wholeApp ? branch.integration === "active" : initialBranchIds.includes(branch.id) && branch.deactivationEligible)).map((branch) => branch.id));
        }
      })
      .catch((e) => current && setError(errorMessage(t, e)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [open, app, storeId, token, t, request, wholeApp, initialBranchIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !app) return null;

  const branches = detail?.branches || [];
  const own = (branch) => Boolean(request) && branch.pendingRequest?.id === request.id;
  const eligible = (branch) => branch.deactivationEligible || own(branch);
  const chosen = branches.filter((branch) => selected.includes(branch.id));
  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  const reasonCode = validateReason(reason);
  const canSubmit = !loading && !busy && !reasonCode && (wholeApp || selected.length > 0);

  const submit = async (event) => {
    event?.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    // Only what a deactivation needs: no account, manager or identifier fields.
    const payload = { ...(wholeApp ? {} : { branchIds: selected }), reason: reason.trim() };
    try {
      if (request) await editChangeRequestAPI(token, storeId, request.id, { version: request.version, ...payload });
      else await createChangeRequestAPI(token, storeId, app.key, { type: "deactivation", ...(wholeApp ? { scope: "store" } : {}), ...payload });
      onDone?.(request ? t.flow.requestUpdated : t.flow.requestSent, "success");
    } catch (e) {
      if (alive.current) setError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const footer = (
    <>
      <Button variant="neutral" appearance="soft" onClick={onClose} disabled={busy}>
        {t.cancel}
      </Button>
      <Button variant="danger" appearance="filled" onClick={submit} disabled={!canSubmit} loading={busy}>
        {request ? t.flow.saveChanges : t.flow.deactivateSubmit}
      </Button>
    </>
  );

  return (
    <LudDashboardModal open title={wholeApp ? t.flow.storeDeactivateTitle(appName) : t.flow.deactivateTitle(appName)} size="lg" onClose={onClose} busy={busy} footer={footer}>
      {error && <NoticeBox variant="danger" size="sm">{error}</NoticeBox>}
      {loading && <p className="lud-app-lead" role="status">{t.loading}</p>}

      {!loading && detail && (
        <form className="lud-app-form" onSubmit={submit} noValidate>
          <NoticeBox variant="warning" size="sm">{wholeApp ? t.flow.storeDeactivateConfirm : t.flow.deactivateConfirm}</NoticeBox>

          {/* Read-only summary of what is affected. */}
          <dl className="lud-app-kv">
            <dt>{t.flow.summaryApp}</dt>
            <dd><strong>{appName}</strong></dd>
            <dt>{wholeApp ? t.flow.affectedBranches : t.flow.selectedBranches}</dt>
            <dd>
              <span className="lud-app-tags">
                {chosen.length ? chosen.map((branch) => <Tags key={branch.id} variant="success" appearance="soft" size="sm">{branch.name}</Tags>) : <small>{t.flow.noBranchesSelected}</small>}
              </span>
            </dd>
          </dl>

          {/* Branch-level: the branches can still be chosen here. */}
          {!wholeApp && (
            <fieldset className="lud-app-fieldset" disabled={busy}>
              <legend>{t.flow.deactivatePick}</legend>
              {!branches.some(eligible) && <NoticeBox variant="warning" size="sm">{t.flow.deactivateNone}</NoticeBox>}
              <BranchPicker
                branches={branches.filter((branch) => eligible(branch) || branch.integration === "active")}
                selected={selected}
                onToggle={toggle}
                reasonFor={(branch) => (eligible(branch) ? "" : t.flow.hasPending)}
                originId={initialBranchIds[0] || null}
                t={t}
                disabled={busy}
              />
            </fieldset>
          )}

          <Input
            textarea
            required
            label={t.flow.reasonLabel}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            helperText={t.flow.reasonHint}
            error={touched && reasonCode ? (reasonCode === "required" ? t.flow.reasonRequired : t.flow.reasonInvalid) : undefined}
            disabled={busy}
          />
        </form>
      )}
    </LudDashboardModal>
  );
}
